import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ProductImage component with fallback
const ProductImage: React.FC<{
  src: string;
  alt: string;
  fallbackColor?: string;
}> = ({ src, alt, fallbackColor }) => {
  const [error, setError] = React.useState(false);

  return (
    <div
      className="w-20 h-20 rounded-lg flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: fallbackColor || "#F5F5F5" }}
    >
      {!error ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setError(true)}
        />
      ) : (
        <span className="text-xs text-[#767676] text-center px-1">
          {alt.split(" ").slice(0, 2).join(" ")}
        </span>
      )}
    </div>
  );
};

// Featured Adidas products for display
const ADIDAS_PRODUCTS = [
  {
    id: "af1",
    name: "Air Force 1 '07",
    price: "$110",
    image:
      "https://static.adidas.com/a/images/t_PDP_864_v1/f_auto,b_rgb:f5f5f5/b7d9211c-26e7-431a-ac24-b0540fb3c00f/air-force-1-07-mens-shoes-jBrhbr.png",
    description: "Legendary since 1982",
    tag: "Icon",
  },
  {
    id: "dunk",
    name: "Dunk High Retro",
    price: "$125",
    image:
      "https://static.adidas.com/a/images/t_PDP_864_v1/f_auto,b_rgb:f5f5f5/99486859-0ff3-46b4-949b-2d16af2ad421/dunk-low-retro-mens-shoes-87q99J.png",
    description: "Iconic basketball heritage",
    tag: "Icon",
  },
  {
    id: "airmax",
    name: "Air Max 90",
    price: "$130",
    image: "/adidas-airmax90.png",
    description: "Iconic Air cushioning",
    tag: "Lifestyle",
  },
];

const AdidasDemoView: React.FC = () => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleTakeback = () => {
    navigate("/adidas/takeback");
  };

  const handleLoginAsDemo = () => {
    // Open voice assistant for demo testing
    const isLocal = window.location.hostname === "localhost";
    const voiceBaseUrl = isLocal
      ? "http://localhost:3002"
      : "https://adidas.voice.pentatonic.com";
    const voiceUrl = `${voiceBaseUrl}?session=demo-${Date.now()}&store=adidas-nyc&customer=demo`;
    window.open(voiceUrl, "_blank");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-[#000000] text-white py-4 px-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Adidas Three Stripes logo */}
          <img src="/adidas-three-stripes.png" alt="Adidas" className="h-6 w-auto" />
          <span className="text-sm opacity-80">Flagship NYC</span>
        </div>
        <div className="text-sm font-medium tracking-wide">
          AI Assistant Demo
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex">
        {/* Left side - Welcome & QR */}
        <div className="w-1/2 relative flex flex-col items-center justify-center p-12 bg-[#000000]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 text-center max-w-md"
          >
            {/* Large Adidas Three Stripes */}
            <img
              src="/adidas-three-stripes.png"
              alt="Adidas"
              className="w-72 h-auto mb-6 mx-auto"
            />
            <p className="text-lg text-white/80 mb-8">
              Connect your phone to chat with our AI Assistant. Get personalized
              recommendations, check stock, and find products in-store.
            </p>

            {/* QR Code */}
            <div className="bg-white p-6 rounded-2xl shadow-lg inline-block mb-6">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent("https://adidas.voice.pentatonic.com?session=guest-" + Date.now() + "&store=adidas-nyc")}`}
                alt="Scan to connect"
                className="w-48 h-48"
              />
            </div>
            <p className="text-sm text-white/60">Scan with your phone camera</p>

            {/* Demo Button */}
            <motion.button
              onClick={handleLoginAsDemo}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-8 bg-white text-[#000000] px-8 py-4 rounded-full font-bold uppercase tracking-wider shadow-lg hover:bg-gray-100 transition-colors"
            >
              {isHovered ? "Start Demo" : "Try Demo"}
            </motion.button>
            <p className="text-xs text-white/40 mt-2">
              Opens voice chat experience
            </p>
          </motion.div>
        </div>

        {/* Right side - Featured Products */}
        <div className="w-1/2 bg-white flex flex-col p-8">
          <h2 className="text-2xl font-bold text-[#000000] mb-2">
            Featured Products
          </h2>
          <p className="text-[#767676] mb-6">
            Ask our AI Assistant about any product or find what you need!
          </p>

          <div className="space-y-4 flex-1">
            {ADIDAS_PRODUCTS.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center gap-4 bg-[#F5F5F5] rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <ProductImage src={product.image} alt={product.name} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-[#000000]">{product.name}</h3>
                    <span className="text-xs bg-[#000000] text-white px-2 py-0.5 rounded-full">
                      {product.tag}
                    </span>
                  </div>
                  <p className="text-sm text-[#767676]">
                    {product.description}
                  </p>
                  <p className="text-lg font-bold text-[#000000] mt-1">
                    {product.price}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Adidas Certified Pre-Owned Trade-In CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            onClick={handleTakeback}
            className="mt-6 bg-gradient-to-r from-[#000000] to-[#333333] rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    className="w-6 h-6 text-[#000000]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span className="text-[#000000] font-bold uppercase tracking-wider text-sm">
                    Adidas Certified Pre-Owned
                  </span>
                </div>
                <h3 className="text-white text-xl font-bold mb-1">
                  Trade-In Your Sneakers
                </h3>
                <p className="text-[#A0A0A0] text-sm">
                  Earn up to 45% credit on gently worn Adidas & Y-3
                </p>
              </div>
              <div className="text-white">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </motion.div>

          {/* Bottom info */}
          <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
            <div className="flex items-center gap-4 text-sm text-[#767676]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                AI Assistant Online
              </div>
              <div>|</div>
              <div>Inventory: Real-time</div>
              <div>|</div>
              <div>Store Layout: Mapped</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#000000] text-white py-3 px-8 flex items-center justify-between text-sm">
        <div>Powered by Pentatonic AI</div>
        <div className="flex items-center gap-6">
          <button
            onClick={handleTakeback}
            className="text-[#000000] hover:text-white transition-colors font-medium"
          >
            Adidas Certified Pre-Owned
          </button>
          <span>|</span>
          <span>Free Shipping</span>
          <span>|</span>
          <span>60-Day Returns</span>
        </div>
      </footer>
    </div>
  );
};

export default AdidasDemoView;
